import { useState } from 'react';

import { exportChartToPng } from '../../utils/exportChart';
import ChartSettingsModal from './ChartSettingsModal';
import {ResponsiveLine} from "@nivo/line";

const LineChart = ({ data, onClick, enablePoints = true, pointSize = 8, lineWidth = 2, enableArea = false, tooltipFormatter=true, threshold, legendPosition = 'bottom-right', onExport, enableZoom = false, showLegend = true }) => {
  // Инициализация состояния настроек из пропсов
  const [settings, setSettings] = useState({
    enablePoints,
    pointSize,
    lineWidth,
    enableArea,
    onExport,
    enableZoom,
    legendPosition,
    showLegend,
    threshold
  });
  // Определяем отступы в зависимости от позиции легенды
  const getMargin = () => {
    const base = { top: 50, right: 50, bottom: 70, left: 60 };
    // Увеличиваем отступ на 30px, если легенда с этой стороны
    if (settings.legendPosition.includes('bottom')) base.bottom = 100;
    if (settings.legendPosition.includes('top')) base.top = 100;
    if (settings.legendPosition.includes('right')) base.right = 100;
    if (settings.legendPosition.includes('left')) base.left = 100;
    return base;
  };

  // Подготовка данных для Nivo LineChart
  const chartData = data.series ?
    data.series.map(series => ({
      id: series.name || 'График',
      data: (series.x || []).map((x, index) => {
        const yValue = (series.y || [])[index];
        // Явно проверяем, что yValue является числом
        return {
          x: x,
          y: typeof yValue === 'number' ? yValue : 0
        };
      }),
      color: series.color || '#4dc9f6'
    })) :
    [
      {
        id: 'Средний балл (%)',
        data: (data.x || []).map((x, index) => {
          const yValue = (data.y || [])[index];
          // Явно проверяем, что yValue является числом
          return {
            x: x,
            y: typeof yValue === 'number' ? yValue : 0
          };
        }),
        color: data.color || '#4dc9f6'
      }
    ];

  // Состояние для модального окна
  const [openSettings, setOpenSettings] = useState(false);

  const handleOpenSettings = () => setOpenSettings(true);
  const handleCloseSettings = () => setOpenSettings(false);

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <div id="line-chart" style={{ height: '100%' }}>
        <ResponsiveLine
          data={chartData}
          margin={getMargin()}
          xScale={{
            type: 'point',
          }}
          yScale={{
            type: 'linear',
            min: 'auto',
            max: 'auto',
            stacked: false,
            reverse: false,
            clamp: true
          }}
          yFormat={(value) => `${Number(value).toFixed(1)}%`}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -45,
            legend: data.xTitle || 'Дата',
            legendPosition: 'middle',
            legendOffset: 60
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: data.yTitle || 'Средний балл (%)',
            legendPosition: 'middle',
            legendOffset: -50
          }}
          pointColor={(point) => {
            // Защита от undefined
            if (!point || !point.data) return data.color || '#4dc9f6'; // Цвет по умолчанию
            // Цвет границы точки такой же, как и заливка
            if (settings.threshold && settings.threshold.value !== undefined && point.data.y < settings.threshold.value) {
              return '#ff0000';
            }
            return point.serieColor;
          }}
          pointBorderWidth={settings.lineWidth}
          pointBorderColor={(point) => {
            // Защита от undefined
            if (!point || !point.data) return data.color || '#4dc9f6'; // Цвет по умолчанию
            // Цвет границы точки такой же, как и заливка
            if (settings.threshold && settings.threshold.value !== undefined && point.data.y < settings.threshold.value) {
              return '#ff0000';
            }
            return point.serieColor;
          }}
          // Заливка точек
          pointSymbol={(point) => {
            // Рисуем круг
            return (
              <circle
                r={point.size / 2}
                fill={point.color}
                stroke={point.borderColor}
                strokeWidth={point.borderWidth}
              />
            );
          }}
          pointLabelYOffset={-12}
          useMesh={true}
          enablePoints={settings.enablePoints}
          pointSize={Math.max(settings.pointSize, 3)}
          enableArea={settings.enableArea}
          lineWidth={settings.lineWidth}
          legends={settings.showLegend ? [{
            anchor: settings.legendPosition,
            translateX: settings.legendPosition.includes('right') ? 100 : settings.legendPosition.includes('left') ? -100 : 0,
            translateY: settings.legendPosition.includes('top') ? -50 : settings.legendPosition.includes('bottom') ? 50 : 0,
            direction: 'column',
            justify: false,
            itemsSpacing: 6,
            itemDirection: 'left-to-right',
            itemWidth: 100,
            itemHeight: 28,
            itemOpacity: 0.9,
            symbolSize: 16,
            symbolShape: 'circle',
            effects: [
              {
                on: 'hover',
                style: {
                  itemOpacity: 1
                }
              }
            ],
            // Устанавливаем z-index легенды выше, чем у кнопок
            style: { pointerEvents: 'none', zIndex: 12 }
          }] : []}
          colors={{ datum: 'color' }}
          animate={true}
          motionConfig="gentle"
          onClick={onClick}
          tooltip={(point) => {
            // Защитная проверка
            if (!point || !point.data) return null;
            // Используем x из point.data, так как xFormatted может быть undefined
            const xValue = point.data.x;
            const yValue = point.data.y;
            // Защитная проверка на xValue
            if (xValue === undefined) return null;
            return (
              <div style={{ 
                padding: '12px', 
                background: '#fff', 
                border: '1px solid #ccc', 
                borderRadius: '8px', 
                fontSize: '13px',
                pointerEvents: 'none',
                zIndex: 13
              }}>
                <strong>{point.serieId}</strong><br/>
                <div>{data.xTitle || 'Дата'}: <strong>{xValue}</strong></div>
                <div>{data.yTitle || 'Средний балл (%)'}: <strong>{yValue !== undefined ? Number(yValue).toFixed(1) + '%' : 'N/A'}</strong></div>
              </div>
            );
          }}
          layers={['grid', 'axes', 'areas', 'crosshair', 'lines', 'points', 'slices', 'mesh',
            (ctx) => {
              if (!settings.threshold || settings.threshold.value === undefined) return null;
              
              // Получаем масштаб Y
              const yScale = ctx.yScale;
              // Получаем значение Y для линии предела
              const y = yScale(settings.threshold.value);
              
              // Проверяем, что значение в пределах видимости
              if (y < 0 || y > ctx.height) return null;
              
              // Используем внутреннюю ширину графика (без отступов)
              const innerWidth = ctx.width - ctx.margin.left - ctx.margin.right;
              
              return (
                <line
                  key="threshold-line"
                  x1={0}
                  y1={y}
                  x2={innerWidth}
                  y2={y}
                  stroke={settings.threshold.color || '#ff0000'}
                  strokeWidth={2}
                  strokeDasharray='6 6'
                  pointerEvents="none"
                />
              );
            },
            'legends'
          ]}
          {...(enableZoom ? { enableSlices: 'x', debugMesh: true, isInteractive: true } : {})}
        />
        {settings.onExport && (
          <button
            onClick={() => exportChartToPng('line-chart', 'график.png')}
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 10,
              padding: '6px 12px',
              fontSize: '12px',
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Экспорт PNG
          </button>
        )}
        <div style={{ position: 'absolute', top: 10, right: 130, zIndex: 10, display: 'inline-block' }}>
          {/* Кнопка с тремя точками */}
          <button
            onClick={handleOpenSettings}
            style={{
              padding: '6px 12px',
              background: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            ⋮
          </button>
        </div>

        <ChartSettingsModal
          open={openSettings}
          onClose={handleCloseSettings}
          settings={settings}
          onUpdate={(newSettings) => {
            // Обновляем локальное состояние настроек
            setSettings(prev => ({ ...prev, ...newSettings }));
            // Опционально: вызываем внешний onClick для уведомления родителя
            if (typeof onClick === 'function') {
              onClick({ type: 'updateSettings', payload: newSettings });
            }
          }}
        />
      </div>
    </div>
  );
};

export default LineChart;